import React, { useState, useEffect, useMemo } from 'react';
import { Box, Collapse, Stack, Typography, TextField } from '@mui/material';

import { ConfigSwitchSection, ParentAwareSaveBar } from '../../common';
import { InheritedConfigSettingCallouts } from '../../common/InheritedConfigSettingCallouts';
import { useChildInheritedConfigEdit } from '../../hooks/useChildInheritedConfigEdit';
import {
  isCallbackInheritedForMerchant,
  isConfigInheritedForMerchant,
} from '../../util/configInheritance';
import Switch from '../../common/Switch';
import { useGetCallbacksQuery, useGetConfigsQuery } from '../../store/modules/merchant/api';
import { useMerchant } from '../../contexts/MerchantContext';
import { FulfillmentState, useFullfillmentSubmit } from '../../hooks/useFulfillmentSubmit';
import { merchantDomainErrorMessage as validateCallbackUrl } from '../../util/helpers';
import PageWrapper from '../../common/PageWrapper';
import type { CallbackType } from '../../store/modules/merchant/types';
import {
  getCallbackDisplayLabel,
  inheritableLabelsFromConfigNames,
  uniqueDisplayLabels,
} from '../../util/parentSettingSave';

function getFulfillmentInheritableSaveBarLabels(
  original: FulfillmentState,
  updated: FulfillmentState,
): string[] {
  const labels: string[] = [];

  if (original.isShippingEnabled !== updated.isShippingEnabled) {
    labels.push(...inheritableLabelsFromConfigNames(['NO_SHIPPING_REQUIRED']));
  }

  const callbackToggles: Array<{
    type: CallbackType;
    wasEnabled: boolean;
    isEnabled: boolean;
  }> = [
    {
      type: 'Products',
      wasEnabled: original.isOrderResolveEnabled,
      isEnabled: updated.isOrderResolveEnabled,
    },
    {
      type: 'ShippingAndTax',
      wasEnabled: original.isOrderChangeEnabled,
      isEnabled: updated.isOrderChangeEnabled,
    },
    {
      type: 'Fulfillability',
      wasEnabled: original.isOrderVerifyEnabled,
      isEnabled: updated.isOrderVerifyEnabled,
    },
  ];

  for (const { type, wasEnabled, isEnabled } of callbackToggles) {
    if (wasEnabled !== isEnabled) {
      labels.push(getCallbackDisplayLabel(type));
    }
  }

  if (original.callbackUrl !== updated.callbackUrl && updated.isOrderResolveEnabled) {
    labels.push(getCallbackDisplayLabel('Products'));
    if (updated.isOrderChangeEnabled) {
      labels.push(getCallbackDisplayLabel('ShippingAndTax'));
    }
    if (updated.isOrderVerifyEnabled) {
      labels.push(getCallbackDisplayLabel('Fulfillability'));
    }
  }

  return uniqueDisplayLabels(labels);
}

const FULFILLMENT_PAGE_INHERITABLE_LABELS = uniqueDisplayLabels([
  ...inheritableLabelsFromConfigNames(['NO_SHIPPING_REQUIRED']),
  getCallbackDisplayLabel('Products'),
  getCallbackDisplayLabel('ShippingAndTax'),
  getCallbackDisplayLabel('Fulfillability'),
]);

const FULFILLMENT_CALLBACK_TYPES = new Set<CallbackType>([
  'Products',
  'ShippingAndTax',
  'Fulfillability',
]);

const FulfillmentComponent = () => {
  const { merchantId, isLoading: merchantInfoLoading, canUpdateMerchantSettings } = useMerchant();
  const { data: callbacks, isLoading: isLoadingCallbacks } = useGetCallbacksQuery(merchantId, {
    skip: !merchantId,
  });
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const noShippingConfig = configs?.find((config) => config.name === 'NO_SHIPPING_REQUIRED');
  const originalFulfillmentState: FulfillmentState = useMemo(
    () => ({
      isShippingEnabled: noShippingConfig?.value !== true,
      isOrderResolveEnabled: !!callbacks?.find((cb) => cb.callbackType === 'Products'),
      isOrderChangeEnabled: !!callbacks?.find((cb) => cb.callbackType === 'ShippingAndTax'),
      isOrderVerifyEnabled: !!callbacks?.find((cb) => cb.callbackType === 'Fulfillability'),
      callbackUrl: callbacks?.find((cb) => cb.callbackType === 'Products')?.url || '',
    }),
    [noShippingConfig, callbacks],
  );

  const { submitFullfillment, isLoading: isSubmitLoading } = useFullfillmentSubmit({
    merchantId,
    originalFulfillmentState,
    callbacks,
    noShippingRequiredConfig: noShippingConfig,
  });

  const [fulfillmentState, setFulfillmentState] =
    useState<FulfillmentState>(originalFulfillmentState);
  const [callbackUrlError, setCallbackUrlError] = useState<string | null>(null);

  const {
    isShippingEnabled,
    isOrderResolveEnabled,
    isOrderChangeEnabled,
    isOrderVerifyEnabled,
    callbackUrl,
  } = fulfillmentState;
  const hasModifyAccess = canUpdateMerchantSettings();
  const readOnly = !hasModifyAccess;

  const shippingInherited = isConfigInheritedForMerchant(merchantId, noShippingConfig);
  const hasInheritedFulfillmentCallback =
    callbacks?.some(
      (callback) =>
        FULFILLMENT_CALLBACK_TYPES.has(callback.callbackType) &&
        isCallbackInheritedForMerchant(merchantId, callback),
    ) ?? false;
  const settingInherited = shippingInherited || hasInheritedFulfillmentCallback;
  const {
    showInheritedCallouts,
    isCustomizingInherited,
    controlsDisabled,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
    isChildInheritedSaveBarOpen,
  } = useChildInheritedConfigEdit(settingInherited, readOnly);

  useEffect(() => {
    setFulfillmentState(originalFulfillmentState);
    resetCustomizing();
  }, [originalFulfillmentState, resetCustomizing]);

  const isFormDirty =
    hasModifyAccess &&
    Object.keys(originalFulfillmentState).some(
      (key) =>
        originalFulfillmentState[key as keyof FulfillmentState] !==
        fulfillmentState[key as keyof FulfillmentState],
    );
  const isSaveBarOpen = isChildInheritedSaveBarOpen(isFormDirty);

  const inheritableSaveBarLabels = useMemo(
    () => getFulfillmentInheritableSaveBarLabels(originalFulfillmentState, fulfillmentState),
    [fulfillmentState, originalFulfillmentState],
  );
  const saveBarInheritance = getSaveBarInheritanceProps(
    isSaveBarOpen,
    inheritableSaveBarLabels,
    FULFILLMENT_PAGE_INHERITABLE_LABELS,
  );

  return (
    <PageWrapper
      showParentHierarchyBanner
      title="Fulfillment"
      description="In order to set your callback URL you first need to enable Shipping"
      isLoading={isLoadingConfigs || isLoadingCallbacks || merchantInfoLoading}
      key={`${configs?.[0]?.merchantId}-${callbacks?.[0]?.merchantId}`}
    >
      <Stack spacing={4}>
        {showInheritedCallouts ? (
          <InheritedConfigSettingCallouts
            isCustomizingInherited={isCustomizingInherited}
            readOnly={readOnly}
            onCustomize={onCustomize}
          />
        ) : null}
        <ConfigSwitchSection
          title="Shipping"
          description="When you turn on shipping, Skipify will display your shipping options to your customers during checkout."
          isChecked={isShippingEnabled}
          onToggle={() =>
            setFulfillmentState((prevState) => ({
              ...prevState,
              isShippingEnabled: !prevState.isShippingEnabled,
            }))
          }
          isLoading={isLoadingConfigs}
          disabled={controlsDisabled}
        />
        <Stack sx={{ maxWidth: '448px' }} spacing={2}>
          <ConfigSwitchSection
            title="Callback URL"
            description={
              <>
                You can designate a URL below where we can perform a callback to get the correct
                and/or tax details based on your customer’s address. This URL can be enabled for the
                following three events available via the SDK:
                <Box component="ul" sx={{ paddingInline: 3, marginBlock: 1 }}>
                  <li>ORDER_RESOLVE - defines amount per line items or cart</li>
                  <li>ORDER_CHANGE - defines tax, shipping address, discounts</li>
                  <li>ORDER_VERIFY - validation before payment processing</li>
                </Box>
                <b> Please make sure to use a valid protocol (http:// or https://)</b>
              </>
            }
            isChecked={isOrderResolveEnabled}
            onToggle={(newIsOrderResolveEnabled) => {
              setFulfillmentState((prevState) => ({
                ...prevState,
                isOrderResolveEnabled: newIsOrderResolveEnabled,
                isOrderChangeEnabled: newIsOrderResolveEnabled && prevState.isOrderChangeEnabled,
                isOrderVerifyEnabled: newIsOrderResolveEnabled && prevState.isOrderVerifyEnabled,
                callbackUrl: newIsOrderResolveEnabled ? prevState.callbackUrl : '',
              }));
              if (!newIsOrderResolveEnabled) {
                setCallbackUrlError(null);
              }
            }}
            renderSwitchLabel={() => (
              <Typography component="span">
                {`Callback URL is currently `}
                <Typography sx={{ fontWeight: 'bold' }} component="span">
                  {`${isOrderResolveEnabled ? 'enabled ' : 'disabled '}`}
                </Typography>
                (<code>ORDER_RESOLVE</code> type)
              </Typography>
            )}
            isLoading={isLoadingCallbacks}
            disabled={controlsDisabled}
          />
          <Collapse in={isOrderResolveEnabled}>
            <Stack spacing={1}>
              <TextField
                required
                label="Callback URL"
                placeholder="Please enter URL"
                disabled={!isOrderResolveEnabled || controlsDisabled}
                value={callbackUrl}
                onChange={(e) =>
                  setFulfillmentState((prevState) => ({
                    ...prevState,
                    callbackUrl: e.target.value,
                  }))
                }
                helperText={callbackUrlError}
                error={!!callbackUrlError}
              />
              <Stack spacing={1}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    sx={{
                      color: isOrderResolveEnabled ? 'grey.900' : 'grey.500',
                      fontSize: '14px',
                    }}
                  >
                    Enable callbacks for the <code>ORDER_CHANGE</code> type
                  </Typography>
                  <Switch
                    isChecked={isOrderChangeEnabled}
                    onToggle={() =>
                      setFulfillmentState((prevState) => ({
                        ...prevState,
                        isOrderChangeEnabled: !prevState.isOrderChangeEnabled,
                      }))
                    }
                    isDisabled={!isOrderResolveEnabled || controlsDisabled}
                    aria-label="Order Change Switch"
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    sx={{
                      color: isOrderResolveEnabled ? 'grey.900' : 'grey.500',
                      fontSize: '14px',
                    }}
                  >
                    Enable callbacks for the <code>ORDER_VERIFY</code> type
                  </Typography>
                  <Switch
                    isChecked={isOrderVerifyEnabled}
                    onToggle={() =>
                      setFulfillmentState((prevState) => ({
                        ...prevState,
                        isOrderVerifyEnabled: !prevState.isOrderVerifyEnabled,
                      }))
                    }
                    isDisabled={!isOrderResolveEnabled || controlsDisabled}
                    aria-label="Order Verify Switch"
                  />
                </Box>
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      </Stack>
      <ParentAwareSaveBar
        show={isSaveBarOpen}
        onSave={() => {
          if (isOrderResolveEnabled) {
            const callbackError = validateCallbackUrl(callbackUrl);
            setCallbackUrlError(callbackError.length > 0 ? callbackError : null);
            if (callbackError.length > 0) return;
          }
          submitFullfillment(fulfillmentState);
        }}
        onCancel={() => {
          setFulfillmentState({ ...originalFulfillmentState });
          setCallbackUrlError(null);
          resetCustomizing();
        }}
        disableSave={!isFormDirty || (isOrderResolveEnabled && callbackUrl.length === 0)}
        saveLoading={isSubmitLoading}
        text={
          isSubmitLoading
            ? 'Saving edits to this page...'
            : 'You are currently configuring this page...'
        }
        inheritableSettingLabels={saveBarInheritance.inheritableSettingLabels}
        detailTextOverride={saveBarInheritance.detailTextOverride}
      />
    </PageWrapper>
  );
};

export default FulfillmentComponent;
